import { Directive, ElementRef, EventEmitter, NgZone, OnDestroy, OnInit, Output } from '@angular/core';

/** How long a stationary press must be held before it counts as a long press. */
const LONG_PRESS_MS = 500;
/** How far a finger may travel and still be a press rather than a scroll. */
const MOVE_TOLERANCE_PX = 12;

/**
 * Emits when a touch or pen press is held still on the element. A held mouse button is ignored (use `contextmenu`
 * there). The click the browser sends when the finger lifts is swallowed, so the press does not also act as a tap.
 *
 * Usage: <div (appLongPress)="openMenu()" (contextmenu)="openMenu(); $event.preventDefault()">
 */
@Directive({ selector: '[appLongPress]', standalone: true })
export class LongPressDirective implements OnInit, OnDestroy {
  @Output() readonly appLongPress = new EventEmitter<void>();

  private pointerId: number | null = null;
  private downX = 0;
  private downY = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private swallowNextClick = false;
  private teardown: Array<() => void> = [];

  constructor(private elementRef: ElementRef<HTMLElement>, private ngZone: NgZone) {}

  ngOnInit(): void {
    // Outside the zone: pointermove fires every frame and changes nothing the view binds to.
    this.ngZone.runOutsideAngular(() => {
      this.listen('pointerdown', (e: PointerEvent) => this.onDown(e));
      this.listen('pointermove', (e: PointerEvent) => this.onMove(e));
      this.listen('pointerup', (e: PointerEvent) => this.onEnd(e));
      this.listen('pointercancel', (e: PointerEvent) => this.onEnd(e));
      this.listen('click', (e: MouseEvent) => this.onClick(e), true);
    });
  }

  ngOnDestroy(): void {
    this.cancel();
    this.teardown.forEach((off) => off());
  }

  private listen(type: string, handler: (e: any) => void, capture = false): void {
    const host = this.elementRef.nativeElement;
    host.addEventListener(type, handler, capture);
    this.teardown.push(() => host.removeEventListener(type, handler, capture));
  }

  private onDown(e: PointerEvent): void {
    // A long press whose click never came must not swallow the next real one.
    this.swallowNextClick = false;
    if (e.pointerType === 'mouse' || !e.isPrimary) return;
    this.cancel();
    this.pointerId = e.pointerId;
    this.downX = e.clientX;
    this.downY = e.clientY;
    this.timer = setTimeout(() => {
      this.cancel();
      this.swallowNextClick = true;
      this.ngZone.run(() => this.appLongPress.emit());
    }, LONG_PRESS_MS);
  }

  private onMove(e: PointerEvent): void {
    if (e.pointerId !== this.pointerId) return;
    if (Math.hypot(e.clientX - this.downX, e.clientY - this.downY) > MOVE_TOLERANCE_PX) this.cancel();
  }

  private onEnd(e: PointerEvent): void {
    if (e.pointerId === this.pointerId) this.cancel();
  }

  private onClick(e: MouseEvent): void {
    if (!this.swallowNextClick) return;
    this.swallowNextClick = false;
    e.preventDefault();
    e.stopPropagation();
  }

  private cancel(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.pointerId = null;
  }
}
